import React, { useState, useEffect, useCallback } from 'react';
import './HRRecognition.css';

const BASE = 'https://fotileams.com/api';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };
}

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1][0] || '')).toUpperCase();
}

function getAvatarColor(name) {
  const colors = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316'];
  let h = 0;
  for (let i = 0; i < (name||'').length; i++) h += name.charCodeAt(i);
  return colors[h % colors.length];
}

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const CATEGORIES = [
  { key: 'overall',    label: 'Top Performer',     icon: '🏆', desc: 'Highest overall score' },
  { key: 'attendance', label: 'Perfect Attendance',icon: '✅', desc: 'Zero absences, zero lates' },
  { key: 'streak',     label: 'Longest Streak',    icon: '🔥', desc: 'Most consecutive on-time days' },
  { key: 'improved',   label: 'Most Improved',     icon: '📈', desc: 'Biggest score increase vs last month' },
];

// ── Announce Modal ────────────────────────────────────────
function AnnounceModal({ category, candidate, onConfirm, onClose }) {
  const [giftNote, setGiftNote] = useState('');
  const [saving, setSaving]     = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    await onConfirm(giftNote);
    setSaving(false);
  };

  const cat = CATEGORIES.find(c => c.key === category);

  return (
    <div className="rec-modal-overlay" onClick={onClose}>
      <div className="rec-modal" onClick={e => e.stopPropagation()}>
        <div className="rec-modal-header">
          <h2>{cat.icon} Announce — {cat.label}</h2>
          <button className="rec-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="rec-modal-body">
          <div className="rec-modal-candidate">
            <div className="rec-modal-av" style={{ background: getAvatarColor(candidate.full_name) }}>
              {getInitials(candidate.full_name)}
            </div>
            <div>
              <div className="rec-modal-name">{candidate.full_name}</div>
              <div className="rec-modal-meta">{candidate.emp_code} · {candidate.department || '—'} · Score: {candidate.score}</div>
            </div>
          </div>
          <label className="rec-modal-label">Gift / Reward Note (optional)</label>
          <textarea className="rec-modal-textarea"
            placeholder="e.g. PKR 3,000 bonus + certificate, or leave blank for recognition only"
            value={giftNote} onChange={e => setGiftNote(e.target.value)} rows={3} />
        </div>
        <div className="rec-modal-footer">
          <button className="rec-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="rec-btn-confirm" onClick={handleConfirm} disabled={saving}>
            {saving ? 'Announcing…' : 'Announce Winner'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────
export default function HRRecognition() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear]   = useState(now.getFullYear());
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalCategory, setModalCategory] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE}/recognition/leaderboard?month=${month}&year=${year}`, { headers: authHeaders() });
      const json = await res.json();
      setData(json);
    } catch {} finally { setLoading(false); }
  }, [month, year]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/recognition/history`, { headers: authHeaders() });
      const json = await res.json();
      setHistory(json.history || []);
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (showHistory) fetchHistory(); }, [showHistory, fetchHistory]);

  const handleAnnounce = async (giftNote) => {
    const candidate = data.candidates[modalCategory];
    try {
      await fetch(`${BASE}/recognition/announce`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          employee_id: candidate.employee_id,
          month, year,
          category: modalCategory,
          score: candidate.score,
          gift_note: giftNote,
        }),
      });
      setModalCategory(null);
      fetchData();
    } catch {}
  };

  const removeAnnouncement = async (id) => {
    if (!window.confirm('Remove this announcement?')) return;
    try {
      await fetch(`${BASE}/recognition/${id}`, { method: 'DELETE', headers: authHeaders() });
      fetchData();
    } catch {}
  };

  const getAnnounced = (category) => data?.announced?.find(a => a.category === category);

  return (
    <div className="rec-root">
      <div className="rec-header">
        <div>
          <h1 className="rec-title">🏆 Employee Recognition</h1>
          <p className="rec-subtitle">Auto-ranked based on attendance, punctuality, and consistency</p>
        </div>
        <button className="rec-history-btn" onClick={() => setShowHistory(!showHistory)}>
          {showHistory ? '← Back to Leaderboard' : '📜 View History'}
        </button>
      </div>

      {!showHistory && (
        <div className="rec-period-pills">
          {MONTHS.map((m, i) => (
            <button key={i} className={`rec-pill ${month === i+1 ? 'rec-pill-active' : ''}`}
              onClick={() => setMonth(i+1)}>{m.slice(0,3)}</button>
          ))}
          <select className="rec-year-select" value={year} onChange={e => setYear(parseInt(e.target.value))}>
            {[year-1, year, year+1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      )}

      {showHistory ? (
        <div className="rec-history-list">
          {history.length === 0 ? (
            <div className="rec-empty">No winners announced yet</div>
          ) : history.map((h, i) => {
            const cat = CATEGORIES.find(c => c.key === h.category);
            return (
              <div key={i} className="rec-history-item">
                <div className="rec-history-icon">{cat?.icon || '🏆'}</div>
                <div className="rec-history-info">
                  <div className="rec-history-winner">{h.full_name} <span className="rec-history-code">({h.emp_code})</span></div>
                  <div className="rec-history-cat">{cat?.label || h.category} — {MONTHS[h.month-1]} {h.year}</div>
                  {h.gift_note && <div className="rec-history-gift">🎁 {h.gift_note}</div>}
                </div>
                <button className="rec-history-remove" onClick={() => removeAnnouncement(h.id)}>✕</button>
              </div>
            );
          })}
        </div>
      ) : loading ? (
        <div className="rec-loading">Calculating rankings…</div>
      ) : data?.leaderboard?.length === 0 ? (
        <div className="rec-empty">No attendance data for {MONTHS[month-1]} {year} yet</div>
      ) : (
        <>
          {/* Category cards */}
          <div className="rec-cat-grid">
            {CATEGORIES.map(cat => {
              const candidate = data?.candidates?.[cat.key];
              const announced = getAnnounced(cat.key);
              return (
                <div key={cat.key} className={`rec-cat-card ${announced ? 'rec-cat-announced' : ''}`}>
                  <div className="rec-cat-header">
                    <span className="rec-cat-icon">{cat.icon}</span>
                    <div>
                      <div className="rec-cat-label">{cat.label}</div>
                      <div className="rec-cat-desc">{cat.desc}</div>
                    </div>
                  </div>

                  {!candidate ? (
                    <div className="rec-cat-none">No qualifying candidate this month</div>
                  ) : (
                    <div className="rec-cat-candidate">
                      <div className="rec-cat-av" style={{ background: getAvatarColor(candidate.full_name) }}>
                        {getInitials(candidate.full_name)}
                      </div>
                      <div className="rec-cat-info">
                        <div className="rec-cat-name">{candidate.full_name}</div>
                        <div className="rec-cat-meta">
                          {cat.key === 'overall'    && `Score: ${candidate.score}/100`}
                          {cat.key === 'attendance' && `${candidate.attendance_rate}% attendance`}
                          {cat.key === 'streak'     && `${candidate.streak} day streak`}
                          {cat.key === 'improved'   && `+${candidate.improvement} points`}
                        </div>
                      </div>
                    </div>
                  )}

                  {announced ? (
                    <div className="rec-announced-badge">
                      ✓ Announced — {announced.full_name}
                      {announced.gift_note && <div className="rec-announced-gift">🎁 {announced.gift_note}</div>}
                      <button className="rec-remove-btn" onClick={() => removeAnnouncement(announced.id)}>Remove</button>
                    </div>
                  ) : candidate ? (
                    <button className="rec-announce-btn" onClick={() => setModalCategory(cat.key)}>
                      Announce Winner
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* Full leaderboard */}
          <div className="rec-leaderboard-title">Full Leaderboard — {MONTHS[month-1]} {year}</div>
          <div className="rec-table">
            <div className="rec-table-head">
              <span>Rank</span>
              <span>Employee</span>
              <span>Department</span>
              <span>Score</span>
              <span>Attendance</span>
              <span>Lates</span>
              <span>Streak</span>
            </div>
            {data.leaderboard.map((emp, i) => (
              <div key={i} className={`rec-table-row ${emp.rank <= 3 ? `rec-rank-${emp.rank}` : ''}`}>
                <span className="rec-rank-cell">
                  {emp.rank === 1 ? '🥇' : emp.rank === 2 ? '🥈' : emp.rank === 3 ? '🥉' : `#${emp.rank}`}
                </span>
                <div className="rec-emp-cell">
                  <div className="rec-table-av" style={{ background: getAvatarColor(emp.full_name) }}>
                    {getInitials(emp.full_name)}
                  </div>
                  <div>
                    <div className="rec-table-name">{emp.full_name}</div>
                    <div className="rec-table-code">{emp.emp_code}</div>
                  </div>
                </div>
                <span className="rec-td">{emp.department || '—'}</span>
                <span className="rec-td rec-score">{emp.score}</span>
                <span className="rec-td">{emp.attendance_rate}%</span>
                <span className="rec-td" style={{ color: emp.late_count > 0 ? '#f59e0b' : '#94a3b8' }}>
                  {emp.late_count}
                </span>
                <span className="rec-td">{emp.streak > 0 ? `🔥 ${emp.streak}` : '—'}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {modalCategory && (
        <AnnounceModal
          category={modalCategory}
          candidate={data.candidates[modalCategory]}
          onConfirm={handleAnnounce}
          onClose={() => setModalCategory(null)}
        />
      )}
    </div>
  );
}